import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { ComplianceBoard } from '../../api/services/document.service';

// The company's compliance, in one line.
//
// The previous version was a full-width red progress bar, which made a 51%
// score look like a catastrophe alarm across the top of every visit. A
// segmented bar says the same thing more usefully: it shows the MIX — how
// much is satisfied, expiring, expired, and never filed — and it does not
// need to be loud to be read.

interface Props {
  board: ComplianceBoard | null;
  onSegmentClick?: () => void;
}

const SEGMENTS = [
  { key: 'satisfied', label: 'On file', color: 'success.main' },
  { key: 'due_soon', label: 'Expiring', color: 'warning.main' },
  { key: 'overdue', label: 'Expired', color: 'error.main' },
  { key: 'lapsed', label: 'Lapsed', color: 'error.dark' },
  { key: 'missing', label: 'Never filed', color: 'grey.300' },
] as const;

const ComplianceStrip: React.FC<Props> = ({ board, onSegmentClick }) => {
  const totals = board?.summary.totals;
  const sum = SEGMENTS.reduce((count, segment) => count + (totals?.[segment.key] ?? 0), 0);
  const score = board?.summary.score ?? null;
  const tracked = !!score && score.total > 0;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ md: 'center' }}
      gap={{ xs: 2, md: 3 }}
      sx={{
        p: 2, mb: 3, borderRadius: 3.5,
        border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 1 }}>
          <Typography variant="h5" sx={{ color: tracked ? 'text.primary' : 'text.disabled' }}>
            {tracked ? `${score!.percent}%` : 'Not tracked'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tracked
              ? `of required paperwork on file · ${score!.satisfied} of ${score!.total}`
              : 'no obligations reconciled yet'}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{ height: 8, borderRadius: 999, overflow: 'hidden', bgcolor: 'grey.200', cursor: onSegmentClick ? 'pointer' : 'default' }}
          onClick={onSegmentClick}
        >
          {sum > 0 && SEGMENTS.map((segment) => {
            const value = totals?.[segment.key] ?? 0;
            if (!value) return null;
            return (
              <Tooltip key={segment.key} title={`${segment.label}: ${value}`}>
                <Box sx={{ width: `${(value / sum) * 100}%`, bgcolor: segment.color }} />
              </Tooltip>
            );
          })}
        </Stack>

        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
          {SEGMENTS.map((segment) => {
            const value = totals?.[segment.key] ?? 0;
            if (!value) return null;
            return (
              <Stack key={segment.key} direction="row" gap={0.6} alignItems="center">
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: segment.color }} />
                <Typography variant="caption" color="text.secondary">
                  {value} {segment.label.toLowerCase()}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      {/* An all-clear vault still needs a reason to come back, so the next
          renewal is shown even when nothing is wrong. */}
      {board?.nextExpiry && (
        <Box
          sx={{
            pl: { md: 3 }, borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' },
            minWidth: { md: 220 },
          }}
        >
          <Typography variant="caption" color="text.disabled" display="block">Next to expire</Typography>
          <Typography variant="body2" fontWeight={600} noWrap>{board.nextExpiry.label}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {board.nextExpiry.ownerLabel}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default ComplianceStrip;
